import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useWallet } from './WalletContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Capacitor } from '@capacitor/core';

export type Level = number; // Levels are numeric: 1, 2, 3, 4, 5, etc.

interface UserStats {
  lifetimeEarnings: number;
  gamesPlayed: number;
  adsWatched: number;
  transactionsCount: number;
}

interface LevelContextType {
  level: Level | null;
  levelProgress: number; // 0-100, progress to next level
  nextLevelEarnings: number; // Earnings needed for next level
  loading: boolean;
  stats: UserStats | null;
  updateLevel: () => Promise<void>;
}

const LevelContext = createContext<LevelContextType | undefined>(undefined);

export const LevelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const { transactions, lifetimeEarnings, loading: walletLoading } = useWallet();
  const [level, setLevel] = useState<Level | null>(null);
  const [levelProgress, setLevelProgress] = useState(0);
  const [nextLevelEarnings, setNextLevelEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  /**
   * Calculate level based on multiple factors:
   * - Lifetime earnings (primary factor, works on all platforms)
   * - Games played (works on all platforms)
   * - Ads watched (native only, but not required)
   * - Transaction count (activity indicator)
   * 
   * Level progression is exponential to create meaningful progression
   */
  const calculateLevel = (userStats: UserStats): { level: Level; progress: number; nextLevelEarnings: number } => {
    const { lifetimeEarnings, gamesPlayed, adsWatched, transactionsCount } = userStats;
    
    // Base level calculation from lifetime earnings (primary factor)
    // Exponential progression: Level 1 = 0-500, Level 2 = 500-1500, Level 3 = 1500-3500, etc.
    let baseLevel = 1;
    let earningsForCurrentLevel = 0;
    let earningsForNextLevel = 500;
    
    if (lifetimeEarnings >= 50000) {
      baseLevel = 10;
      earningsForCurrentLevel = 50000;
      earningsForNextLevel = 75000;
    } else if (lifetimeEarnings >= 30000) {
      baseLevel = 9;
      earningsForCurrentLevel = 30000;
      earningsForNextLevel = 50000;
    } else if (lifetimeEarnings >= 20000) {
      baseLevel = 8;
      earningsForCurrentLevel = 20000;
      earningsForNextLevel = 30000;
    } else if (lifetimeEarnings >= 15000) {
      baseLevel = 7;
      earningsForCurrentLevel = 15000;
      earningsForNextLevel = 20000;
    } else if (lifetimeEarnings >= 10000) {
      baseLevel = 6;
      earningsForCurrentLevel = 10000;
      earningsForNextLevel = 15000;
    } else if (lifetimeEarnings >= 5000) {
      baseLevel = 5;
      earningsForCurrentLevel = 5000;
      earningsForNextLevel = 10000;
    } else if (lifetimeEarnings >= 2000) {
      baseLevel = 4;
      earningsForCurrentLevel = 2000;
      earningsForNextLevel = 5000;
    } else if (lifetimeEarnings >= 1000) {
      baseLevel = 3;
      earningsForCurrentLevel = 1000;
      earningsForNextLevel = 2000;
    } else if (lifetimeEarnings >= 500) {
      baseLevel = 2;
      earningsForCurrentLevel = 500;
      earningsForNextLevel = 1000;
    }
    
    // Activity bonuses (can boost level by +1 max)
    let activityBonus = 0;
    
    // Games played bonus (works on all platforms)
    if (gamesPlayed >= 100) activityBonus = 1;
    else if (gamesPlayed >= 50) activityBonus = 0.5;
    else if (gamesPlayed >= 25) activityBonus = 0.25;
    
    // Ads watched bonus (native only, but not penalized on web)
    if (isNative && adsWatched >= 50) {
      activityBonus = Math.max(activityBonus, 1);
    } else if (isNative && adsWatched >= 25) {
      activityBonus = Math.max(activityBonus, 0.5);
    }
    
    // Transaction count bonus (activity indicator)
    if (transactionsCount >= 100) {
      activityBonus = Math.max(activityBonus, 0.5);
    }
    
    // Final level (base level + activity bonus, max +1)
    const finalLevel = Math.min(baseLevel + Math.floor(activityBonus), baseLevel + 1);
    
    // Calculate progress to next level (0-100%)
    const earningsInCurrentLevel = lifetimeEarnings - earningsForCurrentLevel;
    const earningsNeededForNext = earningsForNextLevel - earningsForCurrentLevel;
    const progress = Math.min(100, Math.max(0, (earningsInCurrentLevel / earningsNeededForNext) * 100));
    
    return {
      level: finalLevel as Level,
      progress: Math.round(progress),
      nextLevelEarnings: earningsForNextLevel,
    };
  };

  // Calculate stats from current data
  const calculateStats = (): UserStats => {
    const gamesPlayed = transactions.filter(t => 
      t.source === 'spin-wheel' || t.source === 'scratch-card'
    ).length;
    
    const adsWatched = transactions.filter(t => 
      t.source === 'rewarded-ad' || t.source === 'interstitial-ad'
    ).length;
    
    return {
      lifetimeEarnings: lifetimeEarnings,
      gamesPlayed,
      adsWatched,
      transactionsCount: transactions.length,
    };
  };

  // Recalculate level and update database
  const recalculateAndUpdateLevel = async () => {
    if (!currentUser) return;

    try {
      const userStats = calculateStats();
      const { level: calculatedLevel, progress, nextLevelEarnings: nextEarnings } = calculateLevel(userStats);
      
      console.log('🔢 Level calculation:', { 
        stats: userStats,
        calculatedLevel,
        progress,
        nextEarnings,
        isNative
      });

      const userRef = doc(db, 'users', currentUser.uid);
      const currentLevel = level;
      
      // Only update if level actually changed
      if (!currentLevel || currentLevel !== calculatedLevel) {
        try {
          await setDoc(userRef, {
            level: calculatedLevel,
            levelProgress: progress,
            nextLevelEarnings: nextEarnings,
            lifetimeEarnings: userStats.lifetimeEarnings,
            gamesPlayed: userStats.gamesPlayed,
            adsWatched: userStats.adsWatched,
            transactionsCount: userStats.transactionsCount,
            levelUpdatedAt: serverTimestamp(),
          }, { merge: true });
          
          setLevel(calculatedLevel);
          setLevelProgress(progress);
          setNextLevelEarnings(nextEarnings);
          setStats(userStats);
          console.log(`✅ Level updated in database: ${currentLevel || 'none'} → ${calculatedLevel}`);
        } catch (writeError) {
          console.warn('⚠️ Could not save level to Firestore (may be blocked by ad blocker):', writeError);
          // Still update locally
          setLevel(calculatedLevel);
          setLevelProgress(progress);
          setNextLevelEarnings(nextEarnings);
          setStats(userStats);
        }
      } else {
        // Level is correct, just update progress and stats
        setLevelProgress(progress);
        setNextLevelEarnings(nextEarnings);
        setStats(userStats);
        try {
          await setDoc(userRef, {
            levelProgress: progress,
            nextLevelEarnings: nextEarnings,
            lifetimeEarnings: userStats.lifetimeEarnings,
            gamesPlayed: userStats.gamesPlayed,
            adsWatched: userStats.adsWatched,
            transactionsCount: userStats.transactionsCount,
          }, { merge: true });
        } catch (error) {
          // Ignore metadata update errors
        }
      }
    } catch (error) {
      console.error('❌ Error recalculating level:', error);
    }
  };

  const updateLevel = async () => {
    await recalculateAndUpdateLevel();
  };

  // Load level from database (primary source of truth)
  useEffect(() => {
    if (!currentUser) {
      // No user - set default and stop loading
      setLevel(1);
      setLevelProgress(0);
      setLoading(false);
      return;
    }
    
    // Wait for wallet to finish loading before loading level
    if (walletLoading) {
      return;
    }

    const loadLevelFromDatabase = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const savedLevel = userData.level as Level | undefined;
          const savedProgress = userData.levelProgress as number | undefined;
          const savedNextEarnings = userData.nextLevelEarnings as number | undefined;
          const savedLifetimeEarnings = userData.lifetimeEarnings as number | undefined;
          
          if (savedLevel) {
            console.log('📋 Loaded level from database:', savedLevel);
            setLevel(savedLevel);
            setLevelProgress(savedProgress || 0);
            setNextLevelEarnings(savedNextEarnings || 0);
            setStats({
              lifetimeEarnings: savedLifetimeEarnings || lifetimeEarnings,
              gamesPlayed: userData.gamesPlayed || 0,
              adsWatched: userData.adsWatched || 0,
              transactionsCount: userData.transactionsCount || 0,
            });
            setLoading(false);
            
            // Check if we need to recalculate (lifetime earnings changed significantly)
            const needsRecalculation = 
              savedLifetimeEarnings === undefined || 
              Math.abs(lifetimeEarnings - savedLifetimeEarnings) > 50; // Changed by more than 50 coins
            
            if (needsRecalculation && !initialLoadDone) {
              // Recalculate in background and update if needed
              setTimeout(() => {
                recalculateAndUpdateLevel();
              }, 1000);
            }
          } else {
            // No level in database, calculate and save it
            console.log('📝 No level in database, calculating...');
            await recalculateAndUpdateLevel();
            setLoading(false);
          }
        } else {
          // User document doesn't exist, calculate and save
          console.log('📝 User document not found, creating with level...');
          await recalculateAndUpdateLevel();
          setLoading(false);
        }
        
        setInitialLoadDone(true);
      } catch (error) {
        console.error('❌ Error loading level from database:', error);
        // Fallback: calculate level from current data
        const userStats = calculateStats();
        const { level: fallbackLevel, progress, nextLevelEarnings: nextEarnings } = calculateLevel(userStats);
        setLevel(fallbackLevel);
        setLevelProgress(progress);
        setNextLevelEarnings(nextEarnings);
        setStats(userStats);
        setLoading(false);
        setInitialLoadDone(true);
      }
    };

    loadLevelFromDatabase();
  }, [currentUser?.uid, walletLoading]);

  // Update level when lifetime earnings or transactions change significantly (after initial load)
  useEffect(() => {
    // Only update after initial load is done
    if (!initialLoadDone || loading || !currentUser || level === null || walletLoading) {
      return;
    }
    
    // Debounce updates to avoid rapid recalculations
    const timeoutId = setTimeout(() => {
      recalculateAndUpdateLevel();
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [lifetimeEarnings, transactions.length]);

  return (
    <LevelContext.Provider value={{ level, levelProgress, nextLevelEarnings, loading, stats, updateLevel }}>
      {children}
    </LevelContext.Provider>
  );
};

export const useLevel = () => {
  const context = useContext(LevelContext);
  if (context === undefined) {
    throw new Error('useLevel must be used within a LevelProvider');
  }
  return context;
};

